"use client";

import * as React from "react";
import { ArrowRight, FlaskConical, GraduationCap, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  id: "application-consultation",
  title: "Application Consultation",
  description: "Method development, chemometric modelling, and feasibility testing tailored to regulated laboratory requirements.",
  href: "/ApplicationConsultationPage",
  icon: FlaskConical,
  tag: "Feasibility & Methods"
}, {
  id: "qualification-lifecycle",
  title: "Qualification & Lifecycle Services",
  description: "Complete IQ/OQ/PQ validation, preventative maintenance schedules, and calibration support across analytical systems.",
  href: "/QualificationLifecycleServicesPage",
  icon: ShieldCheck,
  tag: "Compliance & Uptime"
}, {
  id: "training-application-support",
  title: "Training & Application Support",
  description: "Hands-on operator training, workflow troubleshooting, and dedicated technical helpdesk assistance.",
  href: "/TrainingApplicationSupportPage",
  icon: GraduationCap,
  tag: "Operator Enablement"
}];
export default function SupportServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="supportservices" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-2xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
            Technical Lifecycle
          </div>
          <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Specialist Application & Lifecycle Support
          </h2>
          <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed">
            Practical technical support, compliance verification, and method assistance for regulated analytical laboratories and process teams.
          </p>
        </div>

        {/* 3-Column Service Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <motion.div key={service.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-40px"
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 group">
                  <div>
                    <CardHeader className="space-y-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="size-12 rounded-lg bg-secondary text-primary flex items-center justify-center border border-border group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                          <Icon className="size-6" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                          {service.tag}
                        </span>
                      </div>
                      <CardTitle className="font-default text-xl font-semibold text-card-foreground pt-2">
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pb-6">
                      <p className="font-default text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-0">
                    <Button asChild variant="outline" className="w-full justify-between group-hover:border-primary group-hover:text-primary transition-colors">
                      <Link to={service.href} className="inline-flex items-center justify-between w-full">
                        <span>Explore Service</span>
                        <ArrowRight className="size-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}